import { sendGetRequest, sendPostRequest } from "../../../Client";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";
import { Button, Table, Modal } from "flowbite-react";
import { useState } from "react";

export default function Portfolios() {
  const [openModal, setOpenModal] = useState(false);
  const [inputs, setInputs] = useState({});
  const [modalMode, setModalMode] = useState("add"); // "add" | "edit"
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);

  const navigate = useNavigate();
  const { companyID } = useParams();

  const notyf = new Notyf({
    position: { x: "right", y: "top" },
  });

  const queryClient = useQueryClient();

  // ---- Queries ----
  const { data: portfolios, isLoading } = useQuery({
    queryFn: () => sendGetRequest("portfolios/" + companyID, {}),
    queryKey: ["portfolios", companyID],
  });

  // ---- Notyfs ----
  const deletedNotyf = () => {
    notyf.success({ message: "Portfolio deleted", background: "#de2016" });
  };

  const addedNotyf = () => {
    notyf.success("Portfolio added");
  };

  const editedNotyf = () => {
    notyf.success("Portfolio updated");
  };

  // ---- API helpers ----
  const portfolioDelete = async (id) => {
    await sendPostRequest("portfolio/delete", { portfolioID: id });
  };

  const portfolioAdd = async (path, body) => {
    await sendPostRequest(path, body);
    addedNotyf();
  };

  const portfolioEdit = async (path, body) => {
    await sendPostRequest(path, body);
    editedNotyf();
  };

  // ---- Mutations ----
  const { mutateAsync: doDeleteMutation } = useMutation({
    mutationFn: (id) => portfolioDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios", companyID] });
      deletedNotyf();
    },
  });

  const { mutateAsync: doAddMutation } = useMutation({
    mutationFn: (data) => portfolioAdd(data.path, { ...data.body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios", companyID] });
    },
  });

  const { mutateAsync: doEditMutation } = useMutation({
    mutationFn: (data) => portfolioEdit(data.path, { ...data.body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolios", companyID] });
    },
  });

  // ---- UI handlers ----
  const areYouSure = (id) => {
    Swal.fire({
      title: "Are you sure?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#de3333",
    }).then((result) => {
      if (result.isConfirmed) {
        doDeleteMutation(id);
      }
    });
  };

  function onCloseModal() {
    setOpenModal(false);
    setInputs({});
    setSelectedPortfolio(null);
    setModalMode("add");
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedPortfolio(null);
    setInputs({});
    setOpenModal(true);
  };

  const openEditModal = (portfolio) => {
    setModalMode("edit");
    setSelectedPortfolio(portfolio);
    setInputs({ portfolioName: portfolio.portfolioName });
    setOpenModal(true);
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === "add") {
        await doAddMutation({
          path: "portfolio/add",
          body: {
            companyID: companyID,
            portfolioName: inputs.portfolioName,
          },
        });

        setOpenModal(false);
        setInputs({});
        navigate(`/companies/edit/${companyID}`, {
          replace: true,
          state: { added: 1 },
        });
      } else if (modalMode === "edit" && selectedPortfolio) {

        console.log("Editing portfolio:", selectedPortfolio);
        console.log("New inputs:", inputs.portfolioName);

        await doEditMutation({
          path: "portfolio/update", 
          body: {
            portfolioID: selectedPortfolio.portfolioID,
            portfolioName: inputs.portfolioName,
          },
        });

        setOpenModal(false);
        setInputs({});
        setSelectedPortfolio(null);
      }
    } catch (e) {
      console.log(e);
    }
  };

  if (isLoading) return null;

  return (
    <>
      <div className="flex flex-col p-2 border-2 border-slate-100 rounded-xl">
        <div className="flex flex-row justify-between items-center px-2 mt-2">
          <div>
            <h2 className="text-xl font-semibold leading-7 text-gray-900 px-2 mt-2">
              Portfolios
            </h2>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="text-white bg-slate-700 hover:bg-slate-800 focus:ring-4 focus:ring-slate-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-slate-600 dark:hover:bg-slate-700 focus:outline-none dark:focus:ring-slate-800"
          >
            Add Portfolio
          </button>
        </div>

        <div className="mt-8 flow-root ml-4 mr-4">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              {portfolios.portfolios.length === 0 ? (
                <Table hoverable className="mb-4">
                  <Table.Head>
                    <Table.HeadCell>Portfolio Name</Table.HeadCell>
                    <Table.HeadCell></Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                      <Table.Cell className="w-1/4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        No Portfolios found
                      </Table.Cell>
                      <Table.Cell className="w-3/4"></Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              ) : (
                <Table hoverable className="mb-4">
                  <Table.Head>
                    <Table.HeadCell>Portfolio Name</Table.HeadCell>
                    <Table.HeadCell className="text-right">
                      Actions
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y">
                    {portfolios.portfolios.map((portfolio) => (
                      <Table.Row key={portfolio.portfolioID}>
                        <Table.Cell className="w-9/12 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {portfolio.portfolioName}
                        </Table.Cell>

                        <Table.Cell className="w-3/12 text-right">
                          <div className="inline-flex gap-2">
                            <Button
                              size="sm"
                              color="light"
                              onClick={() => openEditModal(portfolio)}
                            >
                              Edit
                            </Button>

                            <Button
                              size="sm"
                              color="failure"
                              onClick={() => areYouSure(portfolio.portfolioID)}
                            >
                              Delete
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ADD / EDIT MODAL */}
      <Modal show={openModal} size="xl" onClose={onCloseModal} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {modalMode === "add" ? "Add Portfolio" : "Edit Portfolio"}
            </h2>
            <div className="flex flex-row">
              <div className="flex flex-row w-full">
                <div className="w-2/3 p-2">
                  <label
                    htmlFor="portfolioName"
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Portfolio Name
                  </label>
                  <input
                    type="text"
                    id="portfolioName"
                    name="portfolioName"
                    onChange={handleChange}
                    value={inputs.portfolioName || ""}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="w-full ml-2">
              <button
                type="button"
                onClick={handleSubmit}
                className="text-white bg-green-500 hover:bg-green-600 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-green-600 dark:hover:bg-slate-600 focus:outline-none dark:focus:ring-green-600"
              >
                {modalMode === "add" ? "Add Portfolio" : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
